import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, ScrollView, Text, ActivityIndicator } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { makeStyles } from './ScreenSelectionModal.styles';
import ScreenRow from './ScreenRow';
import ModalTitle from '../ModalTitle/ModalTitle';
import { localComms } from '../../../utils/localComms';
import { Screen } from '../../../utils/localComms/interfaces/localComms';
import { useTheme } from '../../../hooks/useTheme';

export interface ScreenSelectionModalProps {
  onClose: () => void;
  onScreenSelected?: (screenIndex: number) => void;
}

const ScreenSelectionModal: React.FC<ScreenSelectionModalProps> = ({
  onClose,
  onScreenSelected,
}) => {
  const { translate } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [savedPreference, setSavedPreference] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadScreens = async () => {
      try {
        const [availableScreens, preference] = await Promise.all([
          localComms.getAvailableScreens?.() ?? Promise.resolve([]),
          localComms.getScreenPreference?.() ?? Promise.resolve(-1),
        ]);
        if (isMounted) {
          setScreens(availableScreens);
          // Treat null (error) the same as -1 (no preference)
          setSavedPreference(preference ?? -1);
        }
      } catch (error) {
        console.error('[ScreenSelectionModal] Failed to load screens:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadScreens();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectScreen = useCallback(
    async (screenIndex: number) => {
      // Prevent race condition from multiple clicks
      if (isMoving) return;

      // Validate screen index bounds
      if (screenIndex < 0 || screenIndex >= screens.length) {
        console.error(
          '[ScreenSelectionModal] Invalid screen index:',
          screenIndex,
        );
        return;
      }

      setIsMoving(true);
      setError(null);

      try {
        // Save preference
        await localComms.setScreenPreference?.(screenIndex);

        // Move to screen
        await localComms.moveToScreen?.(screenIndex);

        // Notify parent
        onScreenSelected?.(screenIndex);

        // Close modal
        onClose();
      } catch (error) {
        console.error('[ScreenSelectionModal] Failed to select screen:', error);
        setError(translate('screenSelection.moveError'));
      } finally {
        setIsMoving(false);
      }
    },
    [onClose, onScreenSelected, isMoving, screens.length, translate],
  );

  // Determine recommended screen: saved preference, or second screen if available
  const recommendedIndex =
    savedPreference >= 0 ? savedPreference : screens.length > 1 ? 1 : 0;

  return (
    <View style={styles.container}>
      <ModalTitle
        title={translate('screenSelection.title')}
        onDismiss={onClose}
      />
      <View style={styles.content}>
        <Text style={styles.description}>
          {translate('screenSelection.description')}
        </Text>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {loading ? (
          <View style={styles.emptyContainer}>
            <ActivityIndicator size="large" color={colors.deepPurple} />
          </View>
        ) : screens.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {translate('screenSelection.noScreens')}
            </Text>
          </View>
        ) : (
          <ScrollView style={styles.list}>
            <View style={styles.rows}>
              {screens.map((screen, index) => (
                <ScreenRow
                  key={screen.id || index}
                  {...screen}
                  index={index}
                  isRecommended={index === recommendedIndex}
                  isDisabled={isMoving}
                  onSelect={handleSelectScreen}
                />
              ))}
            </View>
          </ScrollView>
        )}
      </View>
    </View>
  );
};

export default ScreenSelectionModal;
